/**
 * Générateur — Automatisme : calculer une fréquence simple.
 * Attendu ID : 1127 | Niveau : 4ème | Domaine : Statistiques
 * Rubrique : AUTOMATISME (programme 2026, Statistiques 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - Calculer la fréquence          (saisie en % ; QCM à leurres calculés)
 *   - Les trois écritures            (texte à trous KaTeX : e/total = ?/100 = ? décimal = ? %)
 *   - La fréquence manquante         (saisie : la somme des fréquences fait 100 %)
 *   - Le tableau, vrai ou faux ?     (V/F sur un tableau d'effectifs)
 *   - Fréquence : vrai ou faux ?     (V/F de pur rappel, réactivation)
 *
 * ── Plan de cartes validé (2026-09-07) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Statistiques 4e, automatisme « Calculer une
 * fréquence simple » + grille P1-P12. P1 : CM2 absent (« a chances sur b »),
 * 6e introduit (quotient a/b en fraction, décimal, pourcentage ; définition
 * du pourcentage), 5e formalisé (calculer des fréquences sous trois
 * écritures), 4e automatisme. P2 : sens installés — quotient effectif ÷
 * total, part d'un tout, trois écritures d'un même nombre ; non installés
 * (distracteurs) — l'effectif pris pour la fréquence, le quotient à l'envers,
 * une fréquence > 1. P3 : total dans {10, 20, 25, 50, 100} → passage au
 * pourcentage par × 10, × 5, × 4, × 2 (linéarité de 6e), pourcentages
 * entiers ; pas de simplification de fraction (objectif 4e). P4 :
 * parties-tout. P5 : tableau simple. P6 : aucune calculatrice. P7 : pas de
 * produit en croix. P8 : fréquence, effectif, total, pourcentage, écriture
 * décimale. P9 : 1/4 = 0,25, 50 % / 25 % / 10 % d'un nombre, « 5 fois plus »
 * restent implicites. P10 : constat. P11 : effectif donné à la place de la
 * fréquence ; fréquence hors de [0 ; 1] ; somme des fréquences = 1 ; 0,6 et
 * 60 % (pas 6 %). P12 : rémanence moyenne → carte de réactivation.
 * INVARIANTS :
 *   - le tableau est CONSTRUIT depuis (catégories, effectifs, total) ; total
 *     diviseur de 100, donc pourcentage entier = effectif × (100 ÷ total) ;
 *   - leurres calculés, distincts, entre 0 et 100 ; V/F : pas de crochets,
 *     mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou, fracTrouTex,
} from '../../../utils';
import { tableEnonce, ligneTable, ligneTableEntete } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1127;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FREQ = 'Calculer la fréquence';
const L_ECRITURES = 'Les trois écritures';
const L_MANQUANTE = 'La fréquence manquante';
const L_VF = 'Le tableau, vrai ou faux ?';
const L_RAPPEL = 'Fréquence : vrai ou faux ?';

const MANQUE = '<span style="color:#B45309;font-weight:700">?</span>';
const fr = (x) => String(Math.round(x * 100) / 100).replace('.', ',');
const dec = (pct) => fr(pct / 100);                       // 60 → « 0,6 », 5 → « 0,05 »

// ─── Contextes ─────────────────────────────────────────────────────────────
const CONTEXTES = [
    { id: 'sport', intro: (g) => `On a demandé aux ${g} leur sport préféré.`, entete: 'Sport', cats: ['Football', 'Basket', 'Natation', 'Tennis', 'Danse'] },
    { id: 'transport', intro: (g) => `On a demandé aux ${g} comment ils viennent au collège.`, entete: 'Transport', cats: ['À pied', 'Vélo', 'Bus', 'Voiture'] },
    { id: 'animal', intro: (g) => `On a demandé aux ${g} leur animal préféré.`, entete: 'Animal', cats: ['Chien', 'Chat', 'Poisson', 'Lapin'] },
    { id: 'couleur', intro: (g) => `On a demandé aux ${g} leur couleur préférée.`, entete: 'Couleur', cats: ['Bleu', 'Rouge', 'Vert', 'Jaune'] },
    { id: 'dessert', intro: (g) => `On a demandé aux ${g} leur dessert préféré.`, entete: 'Dessert', cats: ['Fruit', 'Yaourt', 'Gâteau', 'Glace'] },
];
const GROUPES = { 10: ['10 élèves d\'un club', '10 membres d\'une équipe'], 20: ['20 élèves d\'une classe', '20 enfants d\'un centre de loisirs'], 25: ['25 élèves d\'une classe de 4e', '25 membres d\'une association'], 50: ['50 élèves de deux classes', '50 collégiens'], 100: ['100 élèves d\'un collège', '100 personnes'] };
const TOTAUX = [10, 20, 25, 50, 100];

/** Tableau : total diviseur de 100, 3 ou 4 catégories d'effectifs ≥ 1 distincts, pourcentages entiers. */
const tirerTableau = () => {
    for (let essai = 0; essai < 300; essai++) {
        // Revue 2026-09-07 (A8) : sur 100 la fréquence se recopie ; ce total reste un cas de rappel à 10 %.
        const ctx = randomChoice(CONTEXTES); const total = Math.random() < 0.1 ? 100 : randomChoice(TOTAUX.filter((t) => t !== 100)); const k = randInt(3, 4);
        const cats = shuffle(ctx.cats).slice(0, k);
        // effectifs : k − 1 tirés, le dernier complète le total
        const eff = []; let reste = total;
        for (let i = 0; i < k - 1; i++) { const e = randInt(1, Math.floor(reste / 2)); eff.push(e); reste -= e; }
        eff.push(reste);
        if (eff.some((e) => e < 1) || new Set(eff).size !== k) continue;
        const pct = eff.map((e) => (e * 100) / total);
        if (pct.some((p) => !Number.isInteger(p))) continue;
        return { ctx, cats, eff: shuffle(eff), total, groupe: randomChoice(GROUPES[total]) };
    }
    throw new Error('gen_1127 : tableau introuvable');
};
const pctDe = (T, i) => (T.eff[i] * 100) / T.total;

const htmlTableau = (T) => tableEnonce(
    ligneTableEntete([T.ctx.entete, ...T.cats, 'Total']),
    ligneTable('Effectif', [...T.eff.map(String), String(T.total)]),
);

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refS = { v: null }; const refQ = { v: null }; const refE = { v: null }; const refM = { v: null };
const signature = (T, extra) => `${T.ctx.id}-${T.total}-${T.eff.join('x')}-${extra}`;

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Pourcentages faux nommés de la catégorie i : son effectif lu comme un pourcentage, la part des autres (100 − p).
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : en fréquences, la part des autres, la fréquence à l'envers et
// 0,6 lu 6 % ont leurs propres codes.
const erreursFrequence = (T, i) => [['effectif_pour_frequence', T.eff[i]], ['frequence_complementaire', 100 - pctDe(T, i)]];
/** Les erreurs sûres : valeurs entre 0 et 100 exclus, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (v <= 0 || v >= 100 || v === juste) return;
        const deja = parValeur.find((p) => p.v === v);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};

// ─── Carte 1a : calculer la fréquence (saisie en %) ────────────────────────

const construireFreqSaisie = () => {
    const T = tirerTableau(); const i = randInt(0, T.cats.length - 1);
    const texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats.</p>${htmlTableau(T)}<p>Quelle est la fréquence de la catégorie « ${T.cats[i]} », en pourcentage ?</p>`;
    const payload = makeSaisie(String(pctDe(T, i)), texte, { ...opts(L_FREQ), suffixe: '%' });
    payload.contenu = { ...payload.contenu, sousType: `t${T.total}` };
    payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, signature: signature(T, i) };
    const erreurs = erreursSures(erreursFrequence(T, i), pctDe(T, i)).map((e) => ({ code: e.code, valeur: String(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genFreqSaisie = () => antiRepeat(construireFreqSaisie, refS);

// ─── Carte 1b : calculer la fréquence (QCM) ────────────────────────────────

const construireFreqQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const T = tirerTableau(); const i = randInt(0, T.cats.length - 1); const p = pctDe(T, i);
        const autres = T.cats.map((_, j) => j).filter((j) => j !== i);
        const pool = [
            { v: T.eff[i], role: 'effectif' },                                   // l'effectif lu comme un pourcentage
            { v: 100 - p, role: 'complement' },                                  // les autres, pas celle-ci
            { v: pctDe(T, randomChoice(autres)), role: 'autre_categorie' },      // la fréquence d'une autre catégorie
            { v: p + randomChoice([-10, -5, 5, 10]), role: 'voisin' },           // erreur de conversion
            { v: T.total === 100 ? T.eff[i] * 10 : T.eff[i] * (T.total === 10 ? 1 : 10), role: 'mauvais_facteur' },   // multiplié par le mauvais nombre
        ];
        const leurres = [];
        shuffle(pool).forEach((c) => { if (leurres.length < 3 && c.v !== p && c.v > 0 && c.v < 100 && !leurres.some((l) => l.v === c.v)) leurres.push(c); });
        if (leurres.length < 3) continue;
        const options = shuffle([p, ...leurres.map((l) => l.v)].map((v) => `${v} %`));
        const texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats.</p>${htmlTableau(T)}<p>Quelle est la fréquence de la catégorie « ${T.cats[i]} » ?</p>`;
        const payload = makeQcm(options, `${p} %`, texte, opts(L_FREQ));
        payload.contenu = { ...payload.contenu, sousType: `t${T.total}` };
        payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, roles: leurres.map((l) => l.role), signature: signature(T, i) };
        const erreurs = erreursSures(erreursFrequence(T, i), p).map((e) => ({ code: e.code, valeur: `${e.v} %` })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1127 : QCM fréquence sans leurres');
};
const genFreqQcm = () => antiRepeat(construireFreqQcm, refQ);

// ─── Carte 2 : les trois écritures (texte à trous) ─────────────────────────

// Total 100 exclu : le passage de e/total à ?/100 serait une recopie, et c'est la seule étape de calcul de la carte.
const construireEcritures = () => {
    let T = tirerTableau(); while (T.total === 100) T = tirerTableau();
    const i = randInt(0, T.cats.length - 1); const p = pctDe(T, i); const e = T.eff[i];
    // Chaîne d'égalités en KaTeX (demande prof 2026-09-13) : on part de la fréquence brute e sur total, puis les
    // TROIS écritures attendues — fraction sur 100, écriture décimale, pourcentage. Le même nombre est écrit
    // deux fois (numérateur sur 100 et pourcentage) : c'est le sens de la carte, et la banque de mots n'est pas
    // dédupliquée, les deux étiquettes sont donc bien servies.
    // La phrase doit être une ligne entièrement $…$ : un trou posé sur une ligne de texte brut ne serait pas rendu
    // quand la phrase contient une ligne KaTeX (cf. buildMathHtml de texteTrouMath.js).
    const phrase = `$${fracTrouTex(e, T.total)} = ${fracTrouTex(`{{${p}}}`, 100)} = {{${dec(p)}}} = {{${p}}}\\,\\%$`;
    const distracteurs = [String(e), String(T.total), dec(e), fr(p / 10), String(100 - p), fr((100 - p) / 100)].filter((d) => d !== String(p) && d !== dec(p));
    const texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats.</p>${htmlTableau(T)}<p>La fréquence de « ${T.cats[i]} » s'écrit de trois façons. Complète l'égalité : la fraction sur 100, l'écriture décimale, puis le pourcentage.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_ECRITURES), distracteurs: shuffle([...new Set(distracteurs)]).slice(0, 4) });
    payload.contenu = { ...payload.contenu, sousType: `t${T.total}` };
    payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, signature: signature(T, i) };
    // Étiquettes nommées, case par case : l'effectif (e ou e ÷ 100), la part des autres (100 − p, ou en décimal), p ÷ 10 dans la case décimale.
    const nommees = [0, 1, 2].flatMap((trou) => [[trou, String(e), 'effectif_pour_frequence'], [trou, dec(e), 'effectif_pour_frequence'],
        [trou, String(100 - p), 'frequence_complementaire'], [trou, fr((100 - p) / 100), 'frequence_complementaire']]).concat([[1, fr(p / 10), 'pourcent_lu_dixiemes_frequence']]);
    const justes = [String(p), dec(p), String(p)];
    const codesDe = (trou, valeur) => new Set(nommees.filter(([t, v]) => t === trou && v === valeur).map(([, , c]) => c));
    const erreurs = [];
    nommees.forEach(([trou, valeur, code]) => {
        if (!payload.contenu.distracteurs.includes(valeur) || valeur === justes[trou] || codesDe(trou, valeur).size > 1) return;
        if (!erreurs.some((x) => x.trou === trou && x.valeur === valeur)) erreurs.push({ code, trou, valeur });
    });
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genEcritures = () => antiRepeat(construireEcritures, refE);

// ─── Carte 3 : la fréquence manquante (saisie) ─────────────────────────────

const construireManquante = () => {
    const T = tirerTableau(); const i = randInt(0, T.cats.length - 1);
    const pcts = T.cats.map((_, j) => pctDe(T, j));
    const table = tableEnonce(
        ligneTableEntete([T.ctx.entete, ...T.cats, 'Total']),
        ligneTable('Fréquence (%)', [...pcts.map((p, j) => (j === i ? MANQUE : String(p))), '100']),
    );
    const texte = `<p>${T.ctx.intro(T.groupe)} Voici les fréquences, mais l'une d'elles a été effacée.</p>${table}<p>Quelle est la fréquence de la catégorie « ${T.cats[i]} », en pourcentage ?</p>`;
    const payload = makeSaisie(String(pcts[i]), texte, { ...opts(L_MANQUANTE), suffixe: '%' });
    payload.contenu = { ...payload.contenu, sousType: `k${T.cats.length}` };
    payload._meta.params = { ctx: T.ctx.id, cats: T.cats, pcts, i, signature: `${T.ctx.id}-${pcts.join('x')}-${i}` };
    return payload;
};
const genManquante = () => antiRepeat(construireManquante, refM);

// ─── Carte 4 : le tableau, vrai ou faux ? ──────────────────────────────────

let dernieresLignesVf1127 = [];

const genTableauVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const T = tirerTableau(); const i = randInt(0, T.cats.length - 1); const p = pctDe(T, i); const e = T.eff[i];
        const pcts = T.cats.map((_, j) => pctDe(T, j)); const iMax = pcts.indexOf(Math.max(...pcts));
        const autre = randomChoice(T.cats.filter((_, j) => j !== iMax));
        const fauxP = randomChoice([e, 100 - p, pctDe(T, randomChoice(T.cats.map((_, j) => j).filter((j) => j !== i)))].filter((v) => v !== p));
        const candidates = [
            { l: `La fréquence de « ${T.cats[i]} » est ${p} %.`, v: true, f: 'freq' },
            { l: `La fréquence de « ${T.cats[i]} » est ${fauxP} %.`, v: false, f: 'freq' },
            { l: `La fréquence de « ${T.cats[i]} » est ${e}.`, v: false, f: 'freq' },                       // l'effectif pris pour la fréquence
            { l: `En écriture décimale, la fréquence de « ${T.cats[i]} » est ${dec(p)}.`, v: true, f: 'decimal' },
            { l: `En écriture décimale, la fréquence de « ${T.cats[i]} » est ${fr(p / 10)}.`, v: p / 10 === p / 100 ? true : false, f: 'decimal' },
            { l: `La somme de toutes les fréquences est 100 %.`, v: true, f: 'somme' },
            { l: `La somme de toutes les fréquences est ${T.total} %.`, v: T.total === 100, f: 'somme' },
            // Revue 2026-09-08 (A2) : « plus de la moitié » était fausse 6 fois sur 7 ; seuil varié (moitié, quart).
            ...(() => { const seuil = randomChoice([{ l: 'plus de la moitié', v: p > 50 }, { l: 'plus du quart', v: p > 25 }, { l: 'moins du quart', v: p < 25 }]);
                return [{ l: `« ${T.cats[i]} » représente ${seuil.l} des réponses.`, v: seuil.v, f: 'moitie' }]; })(),
            { l: `« ${T.cats[iMax]} » est la catégorie la plus fréquente.`, v: true, f: 'max' },
            { l: `« ${autre} » est la catégorie la plus fréquente.`, v: false, f: 'max' },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesVf1127 = [];
        if (lignes.some((it) => dernieresLignesVf1127.includes(it.l))) continue;
        dernieresLignesVf1127 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>${T.ctx.intro(T.groupe)} Voici les résultats.</p>${htmlTableau(T)}<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i };
        // Le pourcentage faux est nommé par sa valeur ; l'effectif écrit sans %, et p ÷ 10 en écriture décimale, par leur forme.
        const parLigne = new Map([
            ...erreursSures(erreursFrequence(T, i), p).map((x) => [`La fréquence de « ${T.cats[i]} » est ${x.v} %.`, x.code]),
            [`La fréquence de « ${T.cats[i]} » est ${e}.`, 'effectif_pour_frequence'],
            [`En écriture décimale, la fréquence de « ${T.cats[i]} » est ${fr(p / 10)}.`, 'pourcent_lu_dixiemes_frequence'],
        ]);
        const erreurs = lignes.filter((it) => !it.v && parLigne.has(it.l)).map((it) => ({ code: parLigne.get(it.l), ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1127 : V/F tableau sans tirage après 160 essais');
};

// ─── Carte 5 : fréquence, vrai ou faux ? (pur rappel) ──────────────────────

const POOL_RAPPEL = [
    { l: 'La fréquence d\'une catégorie est son effectif divisé par l\'effectif total.', v: true, f: 'definition' },
    { l: 'La fréquence d\'une catégorie est son effectif, c\'est-à-dire le nombre de personnes qui l\'ont choisie.', v: false, f: 'definition', code: 'effectif_pour_frequence' },
    { l: 'La fréquence d\'une catégorie est l\'effectif total divisé par son effectif.', v: false, f: 'definition', code: 'frequence_a_l_envers' },
    { l: 'Une fréquence est toujours comprise entre 0 et 1, c\'est-à-dire entre 0 % et 100 %.', v: true, f: 'bornes' },
    { l: 'Une fréquence peut être plus grande que 1.', v: false, f: 'bornes' },
    { l: 'Une fréquence peut s\'écrire sous forme de fraction, de nombre décimal ou de pourcentage.', v: true, f: 'ecritures' },
    { l: 'Une fréquence s\'écrit uniquement en pourcentage.', v: false, f: 'ecritures' },
    { l: '0,25 et 25 % désignent la même fréquence.', v: true, f: 'conversion' },
    { l: '0,6 et 6 % désignent la même fréquence.', v: false, f: 'conversion', code: 'pourcent_lu_dixiemes_frequence' },
    { l: 'Un quart et 25 % désignent la même fréquence.', v: true, f: 'conversion' },
    { l: 'La somme des fréquences de toutes les catégories vaut 1, soit 100 %.', v: true, f: 'somme' },
    { l: 'La somme des fréquences de toutes les catégories vaut l\'effectif total.', v: false, f: 'somme' },
    { l: 'Si 5 personnes sur 20 choisissent une catégorie, sa fréquence est 25 %.', v: true, f: 'exemple' },
    { l: 'Si 5 personnes sur 20 choisissent une catégorie, sa fréquence est 5 %.', v: false, f: 'exemple', code: 'effectif_pour_frequence' },
    { l: 'Si la moitié des personnes choisissent une catégorie, sa fréquence est 0,5.', v: true, f: 'moitie' },
    { l: 'Si la moitié des personnes choisissent une catégorie, sa fréquence est 2.', v: false, f: 'moitie', code: 'frequence_a_l_envers' },
];

let dernieresLignesRappel1127 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1127 = [];
        if (lignes.some((it) => dernieresLignesRappel1127.includes(it.l))) continue;
        dernieresLignesRappel1127 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les fréquences, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1127 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    frequence_saisie: genFreqSaisie,
    frequence_qcm_unique: genFreqQcm,
    ecritures_texte_trou: genEcritures,
    manquante_saisie: genManquante,
    vf_tableau_vf: genTableauVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', texte_trou: 'Texte à trous', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'frequence', label: L_FREQ, formatsList: buildFormats('frequence') },
    { id: 'ecritures', label: L_ECRITURES, formatsList: buildFormats('ecritures') },
    { id: 'manquante', label: L_MANQUANTE, formatsList: buildFormats('manquante') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
